import React, { useState, useEffect } from 'react';
import { collection, addDoc, Timestamp, getDocs, where, query } from 'firebase/firestore';
import { db } from '../../firebase';
import TaskList from './TaskList';
import { useAuth } from '../Auth/Auth';
import { PlusCircleIcon, MinusCircleIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { toast, ToastContainer } from 'react-toastify';
import { Link, Navigate } from 'react-router-dom';
import BreadCrumb from '../BreadCrumb';
import Select from 'react-select';
import logger from '../../utils/logger';

const pages = [
  { name: 'Service Advisor', to: '/vehicles', component: Link, current: false },
  { name: 'Assign Task to Mechanic', to: '/vehicles/assigntask', component: Link, current: true },
];

const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

const CreateTask = () => {
  const [taskTitle, setTaskTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [employees, setEmployees] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [subtasks, setSubtasks] = useState([{ id: generateId(), description: '', status: 'pending' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, role, loading } = useAuth();
  const assignedBy = user?.email;

  useEffect(() => {
    const fetchJobCards = async () => {
      const querySnapshot = await getDocs(collection(db, "jobs"));
      const fetchedJobCards = querySnapshot.docs.map(doc => ({
        label: `${doc.data().jobCardNo} - ${doc.data().registrationNumber}`,
        value: doc.id,
        ...doc.data()
      }));
      setJobCards(fetchedJobCards);
    };

    fetchJobCards();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      const q = query(collection(db, "employees"), where("role", "==", "Mechanic"));
      const querySnapshot = await getDocs(q);
      setEmployees(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    fetchEmployees();
  }, []);

  const addSubtask = () => {
    setSubtasks([...subtasks, { id: generateId(), description: '', status: 'pending' }]);
  };

  const handleSubtaskChange = (e, index) => {
    const updatedSubtasks = subtasks.map((subtask, i) => i === index ? { ...subtask, description: e.target.value } : subtask);
    setSubtasks(updatedSubtasks);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedEmployee = employees.find(emp => emp.id === assignedTo);
    if (!selectedEmployee || !selectedJob) {
      toast.error("Required fields are missing");
      return;
    }

    setIsSubmitting(true);
    try {
      const startDateTime = new Date(`${startDate}T${startTime}`);
      const deadlineDateTime = new Date(`${deadlineDate}T${deadlineTime}`);
      await addDoc(collection(db, "tasks"), {
        taskTitle,
        startDate: Timestamp.fromDate(startDateTime),
        deadline: Timestamp.fromDate(deadlineDateTime),
        assignedTo: selectedEmployee.id,
        assignedToName: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
        assignedBy,
        status: "new",
        subtasks,
        jobCardNo: selectedJob.jobCardNo,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      });
      toast.success("Task assigned Successfully");
      setTaskTitle('');
      setStartDate('');
      setStartTime('');
      setDeadlineDate('');
      setDeadlineTime('');
      setAssignedTo('');
      setSelectedJob(null);
      setSubtasks([{ id: generateId(), description: '', status: 'pending' }]);
    } catch (error) {
      logger.error("Error adding task: ", error);
      toast.error("Error Adding Task");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteSubtask = (index) => {
    setSubtasks(subtasks.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin' && role !== 'Supervisor') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <>
      <BreadCrumb pages={pages} />
      <div className="min-h-screen bg-gradient-to-br from-emerald-500 via-teal-500 to-teal-600 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <ClipboardDocumentListIcon className="h-10 w-10 text-white mr-3" />
              <h1 className="text-3xl font-black text-white">Task Assignment</h1>
            </div>
            <p className="text-emerald-50 text-sm">Assign detailed tasks to mechanics for accountability and tracking</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Task Form */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Task Title */}
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1">Task Title</label>
                    <input
                      type="text"
                      value={taskTitle}
                      onChange={(e) => setTaskTitle(e.target.value)}
                      placeholder="Enter task title"
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all text-sm"
                      required
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all text-sm"
                      required
                    />
                  </div>

                  {/* Start Time */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all text-sm"
                      required
                    />
                  </div>

                  {/* Deadline Date */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Deadline Date</label>
                    <input
                      type="date"
                      value={deadlineDate}
                      onChange={(e) => setDeadlineDate(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all text-sm"
                      required
                    />
                  </div>

                  {/* Deadline Time */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Deadline Time</label>
                    <input
                      type="time"
                      value={deadlineTime}
                      onChange={(e) => setDeadlineTime(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all text-sm"
                      required
                    />
                  </div>

                  {/* Assign To */}
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1">Assign To Mechanic</label>
                    <select
                      value={assignedTo}
                      onChange={(e) => setAssignedTo(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all text-sm"
                      required
                    >
                      <option value="">Select Mechanic</option>
                      {employees.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.firstName} {employee.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Job Card Selection */}
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1">Select Job Card</label>
                    <Select
                      options={jobCards}
                      getOptionLabel={(option) => `${option.jobCardNo} - ${option.vehicleMake} ${option.vehicleModel}`}
                      getOptionValue={(option) => option.jobCardNo}
                      onChange={(selectedOption) => setSelectedJob(selectedOption)}
                      placeholder="Select a job card"
                      isClearable
                      isSearchable
                      className="text-sm"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '0.75rem',
                          borderWidth: '2px',
                          borderColor: '#d1d5db',
                          '&:hover': { borderColor: '#10b981' },
                          '&:focus-within': { borderColor: '#10b981', boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)' }
                        })
                      }}
                    />
                  </div>
                </div>

                {/* Subtasks Section */}
                <div className="pt-4 border-t-2 border-emerald-100">
                  <div className="flex items-center mb-3">
                    <div className="flex-grow border-b-2 border-emerald-300"></div>
                    <span className="px-3 text-xs font-bold text-emerald-600 uppercase">Task Items</span>
                    <div className="flex-grow border-b-2 border-emerald-300"></div>
                  </div>

                  {subtasks.map((subtask, index) => (
                    <div key={subtask.id} className="flex items-center mb-2">
                      <input
                        type="text"
                        value={subtask.description}
                        onChange={(e) => handleSubtaskChange(e, index)}
                        className="flex-1 px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all text-sm"
                        placeholder="Add task item"
                      />
                      <MinusCircleIcon
                        className="h-6 w-6 ml-2 text-red-500 hover:text-red-700 cursor-pointer transition-colors"
                        onClick={() => deleteSubtask(index)}
                      />
                      {index === subtasks.length - 1 && (
                        <PlusCircleIcon
                          className="h-6 w-6 ml-2 text-emerald-500 hover:text-emerald-700 cursor-pointer transition-colors"
                          onClick={addSubtask}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-6 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold rounded-xl shadow-lg hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Assigning Task...
                    </span>
                  ) : (
                    'Assign Task'
                  )}
                </button>
              </form>
            </div>

            {/* Task List */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <TaskList />
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default CreateTask;
